import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2, Check, CheckSquare, Copy, ExternalLink, KeyRound, LockKeyhole, LogOut, MailCheck,
  LoaderCircle, MailWarning, MapPin, MessageCircle, Pencil, Phone, Search, ShieldCheck, Square, Upload,
  UsersRound, X,
} from "lucide-react";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { branches } from "../data/content";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { countryIsoFromName, countryOptions, phoneDetails } from "../lib/countries";

const indiaStateOptions = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh",
  "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal",
];

function editableMember(member) {
  return {
    ...member,
    currentCity: member.currentCity || "",
    currentState: member.currentState || "",
    currentCountry: member.currentCountry || "",
    phoneNumber: member.phoneNumber || "",
    phoneCountry: member.phoneCountry || countryIsoFromName(member.currentCountry),
    familyNote: member.familyNote || (member.children ? `Children: ${member.children}` : ""),
  };
}

function ProfileEditor({ member, isAdministrator, isOwnProfile, onClose, onSaved, onPortraitSaved }) {
  const [form, setForm] = useState(() => editableMember(member));
  const [portrait, setPortrait] = useState(null);
  const [portraitYear, setPortraitYear] = useState(String(member.photo?.currentYear || new Date().getFullYear()));
  const [portraitSaving, setPortraitSaving] = useState(false);
  const [failedPortraitYear, setFailedPortraitYear] = useState("");
  const [portraitFeedback, setPortraitFeedback] = useState({
    state: "idle",
    message: member.photo?.current
      ? ""
      : "Choose a portrait to save it automatically.",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const name = `${member.firstName} ${member.lastName}`;
  const currentYear = new Date().getFullYear();
  const portraitPreview = useMemo(() => portrait ? URL.createObjectURL(portrait) : "", [portrait]);
  useEffect(() => () => { if (portraitPreview) URL.revokeObjectURL(portraitPreview); }, [portraitPreview]);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    const year = Number(portraitYear);
    if (
      !member.photo?.current || portrait || portraitSaving || failedPortraitYear === portraitYear ||
      !Number.isInteger(year) || year < 1995 || year > currentYear || year === member.photo.currentYear
    ) return undefined;

    setPortraitFeedback({ state: "saving", message: "Saving portrait year…" });
    const timer = window.setTimeout(async () => {
      setPortraitSaving(true);
      try {
        const result = await api(`/api/members/${member.id}`, {
          method: "PATCH",
          body: JSON.stringify({ currentPortraitYear: portraitYear }),
        });
        setPortraitYear(String(result.member.photo?.currentYear || year));
        setPortraitFeedback({ state: "saved", message: "Portrait year saved automatically." });
        onPortraitSaved(result.member);
      } catch (requestError) {
        setFailedPortraitYear(portraitYear);
        setPortraitFeedback({ state: "error", message: requestError.message });
      } finally {
        setPortraitSaving(false);
      }
    }, 650);
    return () => window.clearTimeout(timer);
  }, [currentYear, failedPortraitYear, member.id, member.photo?.current, member.photo?.currentYear, onPortraitSaved, portrait, portraitSaving, portraitYear]);

  async function uploadPortrait(file) {
    const year = Number(portraitYear);
    if (!Number.isInteger(year) || year < 1995 || year > currentYear) {
      setPortraitFeedback({ state: "error", message: `Enter a portrait year from 1995 to ${currentYear}.` });
      return;
    }
    setPortrait(file);
    setPortraitSaving(true);
    setFailedPortraitYear("");
    setPortraitFeedback({ state: "saving", message: "Uploading portrait…" });
    try {
      const body = new FormData();
      body.append("photo", file);
      body.append("yearTaken", portraitYear);
      const result = await api(`/api/members/${member.id}/photo`, { method: "POST", body });
      setPortraitYear(String(result.member.photo?.currentYear || year));
      setPortrait(null);
      setPortraitFeedback({ state: "saved", message: "Portrait saved automatically." });
      onPortraitSaved(result.member);
    } catch (requestError) {
      setPortrait(null);
      setPortraitFeedback({ state: "error", message: requestError.message });
    } finally {
      setPortraitSaving(false);
    }
  }

  function selectPortrait(event) {
    const file = event.currentTarget.files?.[0] || null;
    event.currentTarget.value = "";
    if (file) void uploadPortrait(file);
  }

  function changePortraitYear(value) {
    setFailedPortraitYear("");
    setPortraitYear(value);
  }

  async function save(event) {
    event.preventDefault();
    if (portraitSaving) return;
    setSaving(true);
    setError("");
    try {
      const profile = {
        city: form.city || "",
        state: form.state || "",
        country: "India",
        birthday: form.birthday || "",
        currentRole: form.currentRole || "",
        company: form.company || "",
        currentCity: form.currentCity || "",
        currentState: form.currentState || "",
        currentCountry: form.currentCountry || "",
        email: form.email || "",
        phoneCountry: form.phoneCountry || "",
        phoneNumber: form.phoneNumber || "",
        bio: form.bio || "",
        spouse: form.spouse || "",
        familyNote: form.familyNote || "",
        website: form.website || "",
        linkedin: form.linkedin || "",
        instagram: form.instagram || "",
        facebook: form.facebook || "",
        ...(member.photo?.current && !portrait ? { currentPortraitYear: portraitYear } : {}),
      };
      const result = await api(`/api/members/${member.id}`, { method: "PATCH", body: JSON.stringify(profile) });
      onSaved(result.member);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="profile-editor" role="dialog" aria-modal="true" aria-labelledby="profile-editor-title">
        <header className="profile-editor__header">
          <div><p className="kicker">{isAdministrator && !isOwnProfile ? "Administrator edit" : "My class record"}</p><h2 id="profile-editor-title">{name}</h2><span>{member.branch}</span></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        </header>
        <form className="profile-editor__form" onSubmit={save}>
          <section className="portrait-editor">
            <div className="portrait-editor__pair">
              <figure><span className="portrait-editor__fallback">{member.firstName?.[0]}{member.lastName?.[0]}</span>{member.photo?.college && <img src={`/api/members/${member.id}/photo?type=college`} alt={`${name} in the original 1995 class archive`} onError={(event) => { event.currentTarget.style.display = "none"; }} />}<figcaption><LockKeyhole aria-hidden="true" /> Original · 1995 · locked</figcaption></figure>
              <figure><span className="portrait-editor__fallback">{member.firstName?.[0]}{member.lastName?.[0]}</span>{(portraitPreview || member.photo?.current) && <img src={portraitPreview || `/api/members/${member.id}/photo?type=current&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name}, newer portrait`} onError={(event) => { event.currentTarget.style.display = "none"; }} />}<figcaption>Newer portrait{portrait || member.photo?.current ? portraitYear ? ` · ${portraitYear}` : " · year not recorded" : " · not added"}</figcaption></figure>
            </div>
            <div className="portrait-editor__controls">
              <p className="kicker">Then / now</p><h3>{member.photo?.current ? "Update the newer portrait" : "Add a newer portrait"}</h3><p>The original black-and-white portrait is permanently locked. A newer image is saved beside it, never over it. Use JPEG, PNG, or WebP up to 8 MB.</p>
              <label className={portraitSaving ? "button button--ghost is-disabled" : "button button--ghost"} aria-disabled={portraitSaving}><Upload aria-hidden="true" /> Choose portrait<input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={selectPortrait} disabled={portraitSaving} /></label>
              <label className="form-field">Year picture was taken<input type="number" min="1995" max={currentYear} value={portraitYear} onChange={(event) => changePortraitYear(event.target.value)} required disabled={portraitSaving} /></label>
              {portraitFeedback.message && (
                <div className={`portrait-autosave is-${portraitFeedback.state}`} role={portraitFeedback.state === "error" ? "alert" : "status"} aria-live="polite">
                  {portraitFeedback.state === "saving" && <LoaderCircle aria-hidden="true" />}
                  {portraitFeedback.state === "saved" && <Check aria-hidden="true" />}
                  {portraitFeedback.state === "error" && <X aria-hidden="true" />}
                  <span>{portraitFeedback.message}</span>
                </div>
              )}
            </div>
          </section>
          <section className="profile-edit-section">
            <div className="profile-edit-section__heading"><p className="kicker">Original class record</p><h3>The details we inherited.</h3><p>Correct these fields if the earlier alumni record needs an update.</p></div>
            <div className="form-grid form-grid--three">
              <label className="form-field">Original city<input value={form.city || ""} onChange={(event) => update("city", event.target.value)} /></label>
              <label className="form-field">Original state<select value={form.state || ""} onChange={(event) => update("state", event.target.value)}><option value="">Choose a state</option>{form.state && !indiaStateOptions.includes(form.state) && <option value={form.state}>{form.state}</option>}{indiaStateOptions.map((state) => <option value={state} key={state}>{state}</option>)}</select></label>
              <label className="form-field">Birthday<input type="date" value={form.birthday || ""} onChange={(event) => update("birthday", event.target.value)} /></label>
            </div>
          </section>
          <section className="profile-edit-section">
            <div className="profile-edit-section__heading"><p className="kicker">Where I am now</p><h3>Work, place, and a little context.</h3><p>All fields are optional.</p></div>
            <div className="form-grid form-grid--contact">
              <label className="form-field contact-field--role">Current role<input value={form.currentRole || ""} onChange={(event) => update("currentRole", event.target.value)} /></label>
              <label className="form-field contact-field--company">Company or organization<input value={form.company || ""} onChange={(event) => update("company", event.target.value)} /></label>
              <label className="form-field contact-field--city">Current city<input value={form.currentCity} onChange={(event) => update("currentCity", event.target.value)} /></label>
              <label className="form-field contact-field--state">Current state / region<input value={form.currentState} onChange={(event) => update("currentState", event.target.value)} /></label>
              <label className="form-field contact-field--country">Current country<select value={form.currentCountry} onChange={(event) => update("currentCountry", event.target.value)}><option value="">Choose a country</option>{countryOptions.map((country) => <option value={country.iso} key={country.iso}>{country.name}</option>)}</select></label>
              <label className="form-field contact-field--email">Email address<input type="email" value={form.email || ""} onChange={(event) => update("email", event.target.value)} /></label>
              <label className="form-field contact-field--phone-country">Phone country code<select value={form.phoneCountry} onChange={(event) => update("phoneCountry", event.target.value)}><option value="">Choose a country code</option>{countryOptions.map((country) => <option value={country.iso} key={country.iso}>{country.name} (+{country.callingCode})</option>)}</select></label>
              <label className="form-field contact-field--phone">Phone / WhatsApp number<input inputMode="tel" value={form.phoneNumber} onChange={(event) => update("phoneNumber", event.target.value)} /></label>
            </div>
          </section>
          <section className="profile-edit-section profile-edit-section--about">
            <div className="profile-edit-section__heading"><p className="kicker">About me</p><h3>The journey since MACE.</h3><p>Share the personal and professional chapters you would like classmates to know about.</p></div>
            <div className="form-grid">
              <label className="form-field form-field--wide">My story<textarea rows="8" maxLength="2000" value={form.bio || ""} onChange={(event) => update("bio", event.target.value)} placeholder="Where life took you, the work that matters to you, milestones, interests, lessons, or anything else you would like to share…" /><small>{(form.bio || "").length} / 2000 characters</small></label>
            </div>
          </section>
          <section className="profile-edit-section"><div className="profile-edit-section__heading"><p className="kicker">Family</p><h3>Who shares the story now.</h3><p>Optional details for reconnecting.</p></div><div className="form-grid"><label className="form-field">Spouse&apos;s name<input value={form.spouse || ""} onChange={(event) => update("spouse", event.target.value)} /></label><label className="form-field form-field--wide">A note about the family<textarea rows="3" value={form.familyNote} onChange={(event) => update("familyNote", event.target.value)} /></label></div></section>
          <section className="profile-edit-section"><div className="profile-edit-section__heading"><p className="kicker">Social profiles</p><h3>Make the next hello easier.</h3><p>Paste any complete public profile URLs you would like to share. Every field is optional.</p></div><div className="form-grid"><label className="form-field form-field--wide">LinkedIn profile<input type="url" value={form.linkedin || ""} onChange={(event) => update("linkedin", event.target.value)} placeholder="https://www.linkedin.com/in/…" /></label><label className="form-field">Instagram profile<input type="url" value={form.instagram || ""} onChange={(event) => update("instagram", event.target.value)} placeholder="https://www.instagram.com/…" /></label><label className="form-field">Facebook profile<input type="url" value={form.facebook || ""} onChange={(event) => update("facebook", event.target.value)} placeholder="https://www.facebook.com/…" /></label><label className="form-field form-field--wide">Personal website<input type="url" value={form.website || ""} onChange={(event) => update("website", event.target.value)} placeholder="https://…" /></label></div></section>
          <p className="privacy-note privacy-note--profile"><ShieldCheck aria-hidden="true" /> These profile details and portraits can be viewed only by people signed in to the member room.</p>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div className="profile-editor__actions"><button className="button button--ghost" type="button" onClick={onClose}>Cancel</button><button className="button button--coral" type="submit" disabled={saving || portraitSaving}>{saving ? "Saving…" : portraitSaving ? "Saving portrait…" : "Save changes"} <Check aria-hidden="true" /></button></div>
        </form>
      </section>
    </div>
  );
}

function statusLabel(status) {
  if (status === "active") return "Active";
  if (status === "invited") return "Invite pending";
  return "Not provisioned";
}

function AccessManager({ onClose }) {
  const loader = useCallback(() => api("/api/admin/accounts"), []);
  const { data, error, loading, reload } = useApi(loader, [loader]);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("classmate");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [provisioned, setProvisioned] = useState([]);
  const accounts = data?.accounts || [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter((account) => !needle || [account.firstName, account.lastName, account.branch, account.profileEmail, account.loginEmail, account.status, account.role].join(" ").toLowerCase().includes(needle));
  }, [accounts, query]);
  const filteredIds = filtered.map((account) => account.memberId);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const activationResults = provisioned.filter((result) => result.activationUrl);
  const emailedResults = activationResults.filter((result) => result.emailStatus === "sent");
  const manualResults = activationResults.filter((result) => result.emailStatus !== "sent");

  function toggle(memberId) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(memberId)) next.delete(memberId);
      else if (next.size < 100) next.add(memberId);
      return next;
    });
  }

  function toggleFiltered() {
    setSelected((current) => {
      const next = new Set(current);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => { if (next.size < 100) next.add(id); });
      return next;
    });
  }

  async function provision() {
    setSubmitting(true);
    setActionError("");
    setProvisioned([]);
    try {
      const result = await api("/api/admin/provision", { method: "POST", body: JSON.stringify({ memberIds: [...selected], role }) });
      setProvisioned(result.results || []);
      setSelected(new Set());
      await reload();
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyInvitations() {
    const text = activationResults.map((result) => `${result.name}\n${result.activationUrl}`).join("\n\n");
    await navigator.clipboard.writeText(text);
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="access-manager" role="dialog" aria-modal="true" aria-labelledby="access-manager-title">
        <header className="access-manager__header">
          <div><p className="kicker">Administrator tools</p><h2 id="access-manager-title">Manage class access</h2><p>Create individual accounts in batches of up to 100. New invitations expire after seven days.</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        </header>
        {provisioned.length > 0 && (
          <section className="invitation-results" aria-live="polite">
            <div><p className="kicker">Provisioning complete</p><h3>{emailedResults.length ? `${emailedResults.length} activation email${emailedResults.length === 1 ? "" : "s"} sent` : activationResults.length ? `${activationResults.length} activation link${activationResults.length === 1 ? "" : "s"} ready` : "Roles updated"}</h3><p>{manualResults.length ? `${manualResults.length} invitation${manualResults.length === 1 ? " needs" : "s need"} to be shared manually. ` : ""}Each link can be used once and expires after seven days.</p></div>
            {activationResults.length > 0 && <button className="button button--ink" type="button" onClick={copyInvitations}>Copy all links <Copy aria-hidden="true" /></button>}
            {activationResults.length > 0 && <div className="invitation-links">{activationResults.map((result) => <div key={result.memberId}><span className="invitation-link__person"><b>{result.name}</b><small className={`email-delivery is-${result.emailStatus || "failed"}`}>{result.emailStatus === "sent" ? <><MailCheck aria-hidden="true" /> Sent to {result.suggestedEmail}</> : result.emailStatus === "unavailable" ? <><MailWarning aria-hidden="true" /> No email on file</> : <><MailWarning aria-hidden="true" /> Email failed — share link</>}</small></span><code>{result.activationUrl}</code><button className="icon-button" type="button" aria-label={`Copy activation link for ${result.name}`} onClick={() => navigator.clipboard.writeText(result.activationUrl)}><Copy aria-hidden="true" /></button></div>)}</div>}
          </section>
        )}
        <div className="access-manager__toolbar">
          <label className="search-field"><Search aria-hidden="true" /><span className="sr-only">Search class accounts</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, branch, email…" /></label>
          <button className="button button--ghost" type="button" onClick={toggleFiltered}>{allFilteredSelected ? <CheckSquare aria-hidden="true" /> : <Square aria-hidden="true" />} {allFilteredSelected ? "Clear filtered" : "Select filtered"}</button>
        </div>
        {loading && <Loading label="Opening account register" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="account-list">
          {filtered.map((account) => (
            <label className="account-row" key={account.memberId}>
              <input type="checkbox" checked={selected.has(account.memberId)} onChange={() => toggle(account.memberId)} />
              <span className="account-row__name"><b>{account.firstName} {account.lastName}</b><small>{account.branch}</small></span>
              <span className="account-row__email">{account.loginEmail || account.profileEmail || "No email on file"}</span>
              <span className={`status-badge is-${account.status}`}>{statusLabel(account.status)}</span>
              <span className={`role-badge is-${account.role}`}>{account.role === "administrator" ? "Administrator" : "Classmate"}</span>
            </label>
          ))}
        </div>
        <footer className="access-manager__actions">
          <span><strong>{selected.size}</strong> selected</span>
          <label>Access level<select value={role} onChange={(event) => setRole(event.target.value)}><option value="classmate">Classmate</option><option value="administrator">Administrator</option></select></label>
          <button className="button button--coral" type="button" onClick={provision} disabled={!selected.size || submitting}>{submitting ? "Provisioning…" : role === "administrator" ? "Provision as administrators" : "Provision as classmates"} <KeyRound aria-hidden="true" /></button>
        </footer>
        {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
      </section>
    </div>
  );
}

function memberLocation(member) {
  const countryCode = member.currentCountry || "";
  const countryName = countryOptions.find((option) => option.iso === countryCode)?.name || member.country || countryCode;
  return [member.currentCity || member.city, countryName].filter(Boolean).join(", ");
}

export default function Members({ session, onLogin, onLogout }) {
  const authenticated = Boolean(session?.authenticated);
  const user = session?.user;
  const isAdministrator = user?.role === "administrator";
  const isViewer = user?.role === "viewer";
  const [query, setQuery] = useState("");
  const [branch, setBranch] = useState("All");
  const [selected, setSelected] = useState(null);
  const [managingAccess, setManagingAccess] = useState(false);
  const loader = useCallback(() => authenticated ? api("/api/members") : Promise.resolve({ members: [] }), [authenticated]);
  const { data, error, loading, reload, setData } = useApi(loader, [loader]);
  const members = data?.members || [];
  const filtered = useMemo(() => {
    const needle = query.toLowerCase().trim();
    return members.filter((member) => {
      const matchesBranch = branch === "All" || member.branch === branch;
      const haystack = [member.firstName, member.lastName, member.city, member.currentCity, member.state, member.currentState, member.country, member.currentCountry, member.company, member.currentRole].join(" ").toLowerCase();
      return matchesBranch && (!needle || haystack.includes(needle));
    });
  }, [members, query, branch]);

  async function logout() {
    await api("/api/logout", { method: "POST" });
    onLogout();
  }

  const keepEditorOpen = useCallback((updatedMember) => {
    setData((current) => current ? {
      ...current,
      members: (current.members || []).map((item) => item.id === updatedMember.id ? updatedMember : item),
    } : current);
    setSelected((current) => current?.id === updatedMember.id ? updatedMember : current);
  }, [setData]);

  if (!authenticated) {
    return (
      <section className="members-locked">
        <div className="members-locked__art" aria-hidden="true">
          <p className="kicker">Class register / 1995</p><div className="members-locked__number">304</div>
          <div className="members-locked__caption"><strong>Names remembered.</strong><span>Eight branches.<br />One class.</span></div>
          <span className="members-locked__index">MACE / 91—95</span>
        </div>
        <MemberGate onSuccess={onLogin} />
      </section>
    );
  }

  return (
    <>
      <section className="page-hero page-hero--members">
        <div><p className="kicker">The private class directory</p><h1>Who we were.<br />Who we are now.</h1></div>
        <div className="page-hero__aside">
          <p>{isAdministrator
            ? "You can update any class record and provision individual access for classmates and other administrators."
            : isViewer
              ? "Shared passphrase access is read-only. You can view the class directory, but only named administrators can update other people’s records."
              : "Search the class directory and keep your own record up to date."}</p>
          <div className="member-session-actions">
            {isAdministrator && <button className="button button--yellow" type="button" onClick={() => setManagingAccess(true)}>Manage access <UsersRound aria-hidden="true" /></button>}
            <button className="text-link" type="button" onClick={logout}>Sign out <LogOut aria-hidden="true" /></button>
          </div>
        </div>
      </section>
      <section className="directory section-wrap">
        <div className="directory-toolbar">
          <label className="search-field"><Search aria-hidden="true" /><span className="sr-only">Search members</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, cities, work…" /></label>
          <label className="select-field"><span className="sr-only">Filter by branch</span><select value={branch} onChange={(event) => setBranch(event.target.value)}><option>All</option>{branches.map((item) => <option key={item}>{item}</option>)}</select></label>
          <span className="directory-count">{filtered.length} / {members.length}</span>
        </div>
        {loading && <Loading label="Opening the class register" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="member-list">
          {filtered.map((member, index) => {
            const name = `${member.firstName} ${member.lastName}`;
            const location = memberLocation(member);
            const phone = phoneDetails(member);
            const isOwnProfile = user?.role === "classmate" && user?.memberId === member.id;
            const canEdit = isAdministrator || isOwnProfile;
            return (
              <article className={isOwnProfile ? "member-row is-current" : "member-row"} key={member.id} style={{ "--i": index % 12 }}>
                <div className="member-row__index">{String(index + 1).padStart(3, "0")}</div>
                <div className="member-row__portrait"><img src={`/api/members/${member.id}/photo?type=college`} alt={`${name}, original 1995 portrait`} onError={(event) => { event.currentTarget.style.display = "none"; }} /><span>{member.firstName?.[0]}{member.lastName?.[0]}</span></div>
                <div className="member-row__name"><h2>{name} {isOwnProfile && <small>YOU</small>}</h2><span>{member.branch}</span></div>
                <div className="member-row__detail">{location && <span><MapPin aria-hidden="true" /> {location}</span>}{(member.currentRole || member.company) && <span><Building2 aria-hidden="true" /> {[member.currentRole, member.company].filter(Boolean).join(" · ")}</span>}{phone && <span className="member-row__contact"><a href={phone.telHref}><Phone aria-hidden="true" /> {phone.display}</a>{phone.whatsappHref && <a href={phone.whatsappHref} target="_blank" rel="noreferrer"><MessageCircle aria-hidden="true" /> WhatsApp</a>}</span>}</div>
                <div className="member-row__links">
                  {member.linkedin && <a className="icon-button" href={member.linkedin} target="_blank" rel="noreferrer" aria-label={`${name} on LinkedIn`}><ExternalLink aria-hidden="true" /></a>}
                  {canEdit && <button className="button button--small" type="button" onClick={() => setSelected(member)}>{isOwnProfile ? "Edit my profile" : "View / edit"} <Pencil aria-hidden="true" /></button>}
                </div>
              </article>
            );
          })}
        </div>
      </section>
      {selected && <ProfileEditor member={selected} isAdministrator={isAdministrator} isOwnProfile={user?.memberId === selected.id} onClose={() => setSelected(null)} onPortraitSaved={keepEditorOpen} onSaved={(member) => { setData({ members: members.map((item) => item.id === member.id ? member : item) }); setSelected(null); reload(); }} />}
      {managingAccess && <AccessManager onClose={() => setManagingAccess(false)} />}
    </>
  );
}
